import { AsyncSelect, FormGroup } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IOption } from '@frontend/components/interfaces';
import { getDisplayName } from '@common/utils/getDisplayName';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortableFetch } from '@frontend/utils';

interface IPaymentsPerformerPractitionerFilterProps {
    onChangePerformerPractitioner: (value?: string) => void;
    performerPractitionerId?: string;
}

interface IPerformerPractitioner {
    id: string;
    nameFamily: string;
    nameGiven: string | null;
    nameInfix: string | null;
}

export function PaymentsPerformerPractitionerFilter(props: IPaymentsPerformerPractitionerFilterProps) {
    const [selectedPerformerPractitioner, setSelectedPerformerPractitioner] = useState<IOption<string>>();

    const abortableFetch = useAbortableFetch();
    const language = getSelectedLanguage();

    useEffect(() => {
        if (props.performerPractitionerId) {
            loadPractitioner(props.performerPractitionerId);
        }
    }, [props.performerPractitionerId]);

    return (
        <FormGroup label={language.practitioner.practitioner}>
            <AsyncSelect
                cacheOptions={true}
                defaultOptions={true}
                isClearable={true}
                loadOptions={async (searchTerm) => await loadPerformerPractitionerFilter(searchTerm)}
                noOptionsMessage={() => getSelectedLanguage().common.typeToSearch}
                onChange={(item) => props.onChangePerformerPractitioner(item ? item.value : undefined)}
                value={props.performerPractitionerId ? selectedPerformerPractitioner : undefined}
            />
        </FormGroup>
    );

    async function loadPractitioner(id: string) {
        const result = await abortableFetch.fetchJson(`/api/practitioners/${id}`, 'GET');
        setSelectedPerformerPractitioner({
            label: getDisplayName(result.json as IPerformerPractitioner),
            value: id,
        } as IOption<string>);
    }

    async function loadPerformerPractitionerFilter(searchText: string) {
        const result = await abortableFetch.fetchJson(
            `/api/payments/performerPractitionerFilters?searchText=${searchText}`,
            'GET'
        );
        const options = result.json.items.map((performerPractitioner: IPerformerPractitioner) => ({
            label: getDisplayName(performerPractitioner),
            value: performerPractitioner.id,
        })) as IOption<string>[];

        return options;
    }
}
